import type { Json } from '../lib/newDatabase.types';
import type { IdentityOutcome } from '../lib/itemIdentity';

// Brand type
export interface Brand {
  id: string;
  name: string;
  aliases?: string;
}

// Product Family type
export interface ProductFamily {
  id: string;
  brand_id: string;
  name: string;
  brand?: Brand;
}

// Product Model type
export interface ProductModel {
  id: string;
  family_id: string;
  name: string;
  family?: ProductFamily;
}

// Product Variant type
export interface ProductVariant {
  id: string;
  model_id: string;
  attributes: Json;
  sku: string | null;
  model?: ProductModel;
}

// User Product Status
export type UserProductStatus = 'owned' | 'tracking' | 'removed';

// Removal Reason
export type RemovalReason = 'sold' | 'lost' | 'given_away' | 'other';

// User Product type (replaces InventoryItem)
export interface UserProduct {
  id: string;
  user_id: string;
  variant_id: string;
  condition: string;
  valuation: number | null;
  valuation_source?: string | null;
  restoration_valuation?: boolean;
  restoration_pending?: boolean;
  restoration_market_qualified?: boolean;
  restoration_market_evidence_count?: number;
  restoration_market_reason?: string;
  // V3 item state (P1 columns). identity_status is the mirror of the item's derived identity
  // (item_identity_state); valuation_status / valuation_basis are projector-owned (P8). Item
  // surfaces render through src/lib/itemValuation.ts only.
  valuation_status?: string | null;
  valuation_basis?: string | null;
  identity_status?: 'resolved' | 'unresolved' | 'pending' | null;
  identity_reason?: string | null;
  // Provenance of `condition` (V3 P1/P7): 'user_confirmed' | 'model_inferred' |
  // 'client_default' | 'legacy_unknown'. The write guard validates it.
  condition_source?: 'user_confirmed' | 'model_inferred' | 'client_default' | 'legacy_unknown' | null;
  currency: string | null;
  // 'system' (backend-owned/computed, the default) | 'user' (a human
  // deliberately picked this variant — backend propagation must not
  // overwrite it). Added by the variant-picker feature (T3b migration).
  variant_source?: string;
  purchase_price: number | null;
  purchase_period: string | null;
  images: Json; // Array of image storage paths
  image_analysis: Json; // Metadata from image recognition
  status: UserProductStatus;
  removed_at: string | null;
  removal_price: number | null;
  removal_reason: RemovalReason | null;
  removal_notes: string | null;
  created_at: string;
  updated_at: string;
  variant?: ProductVariant;
}

// Valuation Source type
export interface ValuationSource {
  id: string;
  name: string;
  api_details?: Json;
}

// Variant Valuation type
export interface VariantValuation {
  id: string;
  variant_id: string;
  source_id: string;
  valuation_date: string;
  min_price: number | null;
  max_price: number | null;
  avg_price: number | null;
  currency: string;
  created_at: string;
  source?: ValuationSource;
  variant?: ProductVariant;
}

// Aggregated Variant Valuation type
export interface AggregatedVariantValuation {
  variant_id: string;
  avg_price: number;
  min_price: number;
  max_price: number;
  currency: string;
  last_updated: string;
  variant?: ProductVariant;
}

// Product Tag type
export interface ProductTag {
  id: string;
  name: string;
}

// Product Model Tag type
export interface ProductModelTag {
  model_id: string;
  tag_id: string;
  created_at: string;
  model?: ProductModel;
  tag?: ProductTag;
}

// Type for creating a new user product (form data)
/**
 * V3 P7 (C9): columns the app never writes. Item values belong to the P8 projector and
 * identity state to the backend identity writer; the database guard neutralises such
 * client writes, and userProductStorage strips them before they are sent.
 */
export const SYSTEM_OWNED_USER_PRODUCT_COLUMNS = [
  'valuation',
  'valuation_source',
  'currency',
  'valuation_status',
  'valuation_basis',
  'identity_status',
  'identity_reason',
] as const;
type SystemOwnedColumn = (typeof SYSTEM_OWNED_USER_PRODUCT_COLUMNS)[number];

export type NewUserProduct = Omit<UserProduct, 'id' | 'created_at' | 'updated_at' | SystemOwnedColumn>;

// Type for updating a user product
export type UpdateUserProduct = Partial<
  Omit<UserProduct, 'id' | 'created_at' | 'updated_at' | SystemOwnedColumn>
>;

// Helper type for displaying a user product with related data.
// Every relation is nullable: when variant_id is NULL (backend hasn't
// finished catalog matching), the joined relations come back null and
// shapeUserProduct returns the partial object so the item still renders.
// Consumers must use optional chaining; the type system enforces it.
export interface UserProductWithDetails extends Omit<UserProduct, 'variant'> {
  variant: {
    id: string;
    model_id: string;
    attributes: Json;
    model: {
      id: string;
      family_id: string;
      name: string;
      family: {
        id: string;
        brand_id: string;
        name: string;
        brand: {
          id: string;
          name: string;
          aliases?: string;
        } | null;
      } | null;
    } | null;
  } | null;
  // Convenience fields for UI display
  brand?: {
    name: string;
  };
  productFamily?: {
    name: string;
  };
  productModel?: {
    name: string;
  };
}

// Backend catalog IDs returned by the /run-end-to-end API.
// Used to skip duplicate catalog creation on the frontend.
export interface BackendCatalogData {
  instrument_id: number | null;
  variant_id: string | null;
  brand_id: string | null;
  family_id: string | null;
  model_id: string | null;
  sku: string | null;
  valuation_source?: 'market_data' | 'gemini_estimate' | null;
  gemini_estimate?: number | null;
  price_source: 'market_data' | 'gemini_estimate' | 'none';
  price_discovery_status: 'complete' | 'pending' | 'not_needed';
  price_discovery_job_id: number | null;
  product_id?: string; // Frontend-generated UUID, used as user_products.id to link with user_identified_items
  identity?: IdentityOutcome | null; // T1 scan identity outcome (absent when the backend flag is off)
}

// Shape of user_products.image_analysis JSON column. All fields optional —
// older rows may lack newer keys (brand/model/family/valuation_min/max),
// and confidenceScore (camelCase) appears in legacy data.
export interface ImageAnalysisMetadata {
  brand?: string;
  model?: string;
  family?: string;
  category?: string;
  description?: string;
  condition_score?: number;
  demand_score?: number;
  confidence_score?: number;
  confidenceScore?: number;
  variant_fields?: Array<{ name: string; value: string }>;
  valuation_min?: number | null;
  valuation_max?: number | null;
}

export function parseImageAnalysis(value: Json | null | undefined): ImageAnalysisMetadata {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return value as ImageAnalysisMetadata;
}
